import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Brush,
} from "recharts";
import {
  fetchCorporateHistory,
  fetchCorporateHistoryLocations,
  fetchCorporateHistoryYears,
  fetchFuelTypes,
} from "../api/client";
import { toPng } from "html-to-image";
import { useTranslation } from "react-i18next";
import { buildTrendChart, buildTrendDayValues } from "../utils/trendSeries.js";
import { filterTrendRows } from "../utils/trendFilters.js";

const PROVIDER_LABELS = {
  neste_corp: "Neste",
  st1_for_corporate: "St1",
  abc_corp: "ABC",
};

const seriesColors = [
  "#2563eb", "#dc2626", "#16a34a", "#9333ea", "#ea580c",
  "#0891b2", "#be123c", "#4f46e5", "#65a30d", "#a16207",
];

const ECO_FUEL_TYPE_ID = "burning_oil_eco";

const localeCompareFi = (a = "", b = "") =>
  a.localeCompare(b, "fi", { sensitivity: "base" });
const formatChartDate = (value) => {
  if (typeof value !== "string") return value;
  const [, month, day] = value.slice(0, 10).split("-");
  return month && day ? `${day}.${month}.` : value;
};

function formatProductName(item) {
  const baseName =
    item.product_name || item.product_code || item.product_group || "Tuote";
  const resolvedName = baseName;
  if (
    (item.fuelTypeId && item.fuelTypeId === ECO_FUEL_TYPE_ID) ||
    /eco-öljy/i.test(item.fuelTypeLabel || "")
  ) {
    return /hvo/i.test(resolvedName)
      ? resolvedName
      : `${resolvedName} (RE HVO)`;
  }
  return resolvedName;
}

function normalizeHistory(items = []) {
  return items
    .map((item) => {
      const provider = item.provider || "unknown";
      const location =
        item.municipality || item.postal_code || item.location || "Unknown";
      const postalCode =
        item.postal_display_code ||
        item.postal_code ||
        "";
      const displayName =
        item.postal_display_name ||
        item.municipality ||
        item.location ||
        location;
      const locationKey = postalCode || displayName || location;
      const productName = formatProductName(item);
      const unitPrice =
        typeof item.unit_price === "number"
          ? item.unit_price
          : typeof item.unit_price_excl === "number"
          ? item.unit_price_excl
          : typeof item.unit_price_incl === "number"
          ? item.unit_price_incl
          : null;

      if (unitPrice === null) return null;

      return {
        provider,
        providerLabel: PROVIDER_LABELS[provider] || provider,
        location: displayName,
        locationName: displayName,
        locationCode: postalCode,
        locationKey,
        productName,
        date: item.report_date,
        unit_price: unitPrice,
        categoryId: item.categoryId || null,
        categoryLabel: item.categoryLabel || null,
        fuelTypeId: item.fuelTypeId || null,
        fuelTypeLabel: item.fuelTypeLabel || null,
      };
    })
    .filter(Boolean);
}

function pickDefaultLocationId(list = []) {
  const rovaniemi = list.find((loc) =>
    (loc.label || "").toUpperCase().includes("ROVANIEMI")
  );
  return (rovaniemi && rovaniemi.id) || (list[0] && list[0].id) || null;
}

function mergeLocationOptions(existing = [], additions = []) {
  const byId = new Map(existing.map((location) => [location.id, location]));
  additions.forEach((location) => byId.set(location.id, location));
  return [...byId.values()].sort((a, b) => localeCompareFi(a.label, b.label));
}

function MultiSelection({
  title,
  options,
  selected,
  onToggle,
  onSelectAll,
  onClear,
  allLabel,
  clearLabel,
  selectedLabel,
  noneSelectedLabel,
}) {
  const selectedLabels = selected.map(
    (value) => options.find((option) => option.id === value)?.label || value
  );
  return (
    <div>
      <p className="block mb-1 font-medium">{title}</p>
      <div className="flex gap-2 mb-1">
        <button type="button" onClick={onSelectAll} className="bg-blue-500 text-white px-2 py-1 rounded text-sm hover:bg-blue-600">{allLabel}</button>
        <button type="button" onClick={onClear} className="bg-gray-300 text-gray-800 px-2 py-1 rounded text-sm hover:bg-gray-400">{clearLabel}</button>
      </div>
      <div className="max-h-48 overflow-y-auto rounded border bg-white">
        {options.map((option) => (
          <label key={option.id} className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100">
            <input type="checkbox" checked={selected.includes(option.id)} onChange={() => onToggle(option.id)} />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
      <p className="mt-2 text-xs font-medium text-gray-600">{selectedLabel}</p>
      <div className="mt-1 min-h-8 rounded border bg-gray-50 p-1">
        {selected.length > 0 ? selectedLabels.map((label, index) => (
          <button key={`${selected[index]}-${label}`} type="button" onClick={() => onToggle(selected[index])} className="m-0.5 rounded bg-blue-100 px-2 py-1 text-left text-xs text-blue-900 hover:bg-blue-200">{label} ×</button>
        )) : <p className="px-1 py-1 text-xs text-gray-500">{noneSelectedLabel}</p>}
      </div>
    </div>
  );
}

export default function PriceTrends() {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const [locations, setLocations] = useState([]);
  const [locationSearchInput, setLocationSearchInput] = useState("");
  const [locationQuery, setLocationQuery] = useState("");
  const [locationBrowseMode, setLocationBrowseMode] = useState("scheduled");
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [providers] = useState(Object.keys(PROVIDER_LABELS));
  const [products, setProducts] = useState([]);
  const [fuelCategories, setFuelCategories] = useState([]);
  const [fuelTypes, setFuelTypes] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const chartContainerRef = useRef(null);

  const [selectedLocations, setSelectedLocations] = useState([]);
  const selectedLocationsRef = useRef([]);
  const [selectedProviders, setSelectedProviders] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedFuelTypes, setSelectedFuelTypes] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);

  const today = new Date();
  const defaultEnd = today.toISOString().slice(0, 10);
  const defaultStart = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [isQuarterRange, setIsQuarterRange] = useState(false);
  const [hoveredDate, setHoveredDate] = useState("");
  const [hoveredSeriesKey, setHoveredSeriesKey] = useState(null);
  const [pointerPosition, setPointerPosition] = useState(null);

  useEffect(() => {
    selectedLocationsRef.current = selectedLocations;
  }, [selectedLocations]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setLocationQuery(locationSearchInput.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [locationSearchInput]);

  useEffect(() => {
    const controller = new AbortController();
    let current = true;
    setIsLoadingLocations(true);
    fetchCorporateHistoryLocations({
      startDate,
      endDate,
      query: locationQuery || undefined,
      scope: locationBrowseMode === "scheduled" ? "scheduled" : undefined,
      all: locationBrowseMode === "history",
      limit: 50,
      signal: controller.signal,
    })
      .then((response) => {
        if (!current) return;
        const initialLocations = response.recommended && !response.items.some((item) => item.id === response.recommended.id)
          ? [response.recommended, ...response.items]
          : response.items;
        setLocations((previous) => {
          const retained = previous.filter((location) => selectedLocationsRef.current.includes(location.id));
          return mergeLocationOptions(retained, initialLocations);
        });
        setSelectedLocations((previous) => {
          if (previous.length) return previous;
          const defaultId = response.recommended?.id || pickDefaultLocationId(initialLocations);
          return defaultId ? [defaultId] : [];
        });
      })
      .catch((err) => {
        if (err.code === "ERR_CANCELED" || !current) return;
        setError(err.response?.data?.message || t("trends.errors.locations"));
        setLocations([]);
      })
      .finally(() => {
        if (current) setIsLoadingLocations(false);
      });
    return () => {
      current = false;
      controller.abort();
    };
  }, [startDate, endDate, locationQuery, locationBrowseMode, t]);

  const showLocationList = (mode) => {
    setLocationSearchInput("");
    setLocationQuery("");
    setLocationBrowseMode(mode);
  };

  useEffect(() => {
    if (selectedLocations.length === 0) {
      setData([]);
      return undefined;
    }
    const controller = new AbortController();
    let current = true;
    setIsLoadingData(true);
    fetchCorporateHistory({
      startDate,
      endDate,
      locations: selectedLocations,
      providers: selectedProviders,
      signal: controller.signal,
    })
      .then((response) => {
        if (!current) return;
        const normalized = normalizeHistory(response.items);
        setData(normalized);
        setProducts([...new Set(normalized.map((row) => row.productName))]);
        setError(null);
      })
      .catch((err) => {
        if (err.code === "ERR_CANCELED" || !current) return;
        const errorCode = err.response?.data?.code;
        setError(
          errorCode === "INVALID_DATE" ? t("trends.errors.invalidDate")
            : errorCode === "INVALID_DATE_RANGE" ? t("trends.errors.missingDate")
            : errorCode === "INVALID_DATE_ORDER" ? t("trends.errors.dateOrder")
            : errorCode === "DATE_RANGE_TOO_LARGE" ? t("trends.errors.dateRange")
            : err.response?.data?.message || t("trends.errors.history")
        );
        setData([]);
      })
      .finally(() => {
        if (current) setIsLoadingData(false);
      });
    return () => {
      current = false;
      controller.abort();
    };
  }, [startDate, endDate, selectedLocations, selectedProviders, t]);

  useEffect(() => {
    fetchCorporateHistoryYears()
      .then(({ years }) => {
        const nextYears = Array.isArray(years) ? years : [];
        setAvailableYears(nextYears);
        setSelectedYear((previousYear) =>
          nextYears.includes(Number(previousYear))
            ? previousYear
            : String(nextYears[0] || "")
        );
      })
      .catch(() => setAvailableYears([]));
  }, []);

  useEffect(() => {
    fetchFuelTypes()
      .then((groups) => {
        setFuelCategories(
          (groups || []).map((group) => ({ id: group.id, label: group.label }))
        );
        const variants = [];
        (groups || []).forEach((group) => {
          group.variants?.forEach((variant) => {
            variants.push({
              id: variant.id,
              label: `${group.label} – ${variant.label}`,
              categoryId: group.id,
            });
          });
        });
        setFuelTypes(variants);
      })
      .catch(() => setFuelCategories([]));
  }, []);

  const handleSelectAll = (list, setter) => setter(list);
  const handleClearAll = (setter) => setter([]);
  const toggleSelection = (value, setter) => {
    setter((previous) => previous.includes(value)
      ? previous.filter((item) => item !== value)
      : [...previous, value]);
  };

  const applyRange = (days) => {
    const today = new Date();
    const end = today.toISOString().slice(0, 10);
    const start = new Date(today.getTime() - days * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);
    setStartDate(start);
    setEndDate(end);
    setIsQuarterRange(false);
  };

  const applyQuarter = (quarterIndex) => {
    const year = Number(selectedYear);
    if (!Number.isInteger(year)) return;

    const start = new Date(Date.UTC(year, quarterIndex * 3, 1));
    const end = new Date(Date.UTC(year, quarterIndex * 3 + 3, 0));
    const today = new Date();
    const effectiveEnd = end > today ? today : end;
    setStartDate(start.toISOString().slice(0, 10));
    setEndDate(effectiveEnd.toISOString().slice(0, 10));
    setIsQuarterRange(true);
    setSelectedLocations((previousLocations) => previousLocations.slice(0, 5));
  };

  const isQuarterAvailable = (quarterIndex) => {
    const year = Number(selectedYear);
    if (!Number.isInteger(year)) return false;
    return new Date(Date.UTC(year, quarterIndex * 3, 1)) <= new Date();
  };

  const downloadChartSnapshot = async () => {
    const chart = chartContainerRef.current?.querySelector(".recharts-wrapper");
    if (!chart) return;

    try {
      const imageUrl = await toPng(chart, {
        backgroundColor: "#ffffff",
        cacheBust: true,
        pixelRatio: 2,
      });
      const link = document.createElement("a");
      link.href = imageUrl;
      link.download = `hintatrendi-${startDate || "chart"}-${endDate || "chart"}.png`;
      link.click();
    } catch (err) {
      console.error("Failed to save chart snapshot:", err);
      setError(t("trends.errors.saveChart"));
    }
  };

  const toggleLocation = (locationId) => {
    setSelectedLocations((previous) => {
      if (previous.includes(locationId)) return previous.filter((id) => id !== locationId);
      return isQuarterRange ? [...previous, locationId].slice(0, 5) : [...previous, locationId];
    });
  };

  const filteredData = useMemo(() => filterTrendRows(data, {
    locations: selectedLocations,
    providers: selectedProviders,
    products: selectedProducts,
    categories: selectedCategories,
    fuelTypes: selectedFuelTypes,
  }), [data, selectedLocations, selectedProviders, selectedProducts, selectedCategories, selectedFuelTypes]);

  const availableFuelTypes = useMemo(() => {
    if (selectedCategories.length === 0) return fuelTypes;
    const allowed = new Set(selectedCategories);
    return fuelTypes.filter((type) => allowed.has(type.categoryId));
  }, [fuelTypes, selectedCategories]);

  useEffect(() => {
    setSelectedFuelTypes((prev) =>
      prev.filter((id) => availableFuelTypes.some((type) => type.id === id))
    );
  }, [availableFuelTypes]);

  const availableProducts = useMemo(() => {
    const allowedCategories =
      selectedCategories.length > 0 ? new Set(selectedCategories) : null;
    const allowedFuelTypes =
      selectedFuelTypes.length > 0 ? new Set(selectedFuelTypes) : null;
    const productNames = new Set();
    data.forEach((row) => {
      if (
        allowedCategories &&
        (!row.categoryId || !allowedCategories.has(row.categoryId))
      ) {
        return;
      }
      if (
        allowedFuelTypes &&
        (!row.fuelTypeId || !allowedFuelTypes.has(row.fuelTypeId))
      ) {
        return;
      }
      productNames.add(row.productName);
    });

    if (productNames.size === 0) {
      return [...products].sort((a, b) => localeCompareFi(a, b));
    }
    return Array.from(productNames).sort((a, b) => localeCompareFi(a, b));
  }, [data, products, selectedCategories, selectedFuelTypes]);

  useEffect(() => {
    setSelectedProducts((prev) =>
      prev.filter((name) => availableProducts.includes(name))
    );
  }, [availableProducts]);

  const { chartData, chartSeries } = useMemo(
    () => buildTrendChart(filteredData),
    [filteredData]
  );
  useEffect(() => {
    setHoveredDate((previous) =>
      chartData.some((item) => item.date === previous)
        ? previous
        : chartData.at(-1)?.date || ""
    );
  }, [chartData]);
  const hoveredValues = useMemo(() => {
    const dateValues = buildTrendDayValues(chartData, chartSeries, hoveredDate);
    return dateValues.map((item) => ({
      ...item,
      color: seriesColors[chartSeries.findIndex((series) => series.key === item.key) % seriesColors.length],
    }));
  }, [chartData, chartSeries, hoveredDate]);
  const hoveredLine = hoveredValues.find((item) => item.key === hoveredSeriesKey);

  return (
    <div className="p-6 space-y-8">
      <h1 className="text-xl font-bold mb-4">{t("trends.title")}</h1>
      {isLoadingData && <p className="text-sm text-gray-600">{t("trends.loading")}</p>}
      {error && <p className="text-red-500">{error}</p>}

      {/* Date range filter */}
      <div className="flex flex-wrap items-end gap-4 mb-4">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium text-gray-700">{t("trends.quickChoices")}</span>
          <button
            onClick={() => applyRange(7)}
            className="px-3 py-1 rounded bg-blue-500 text-white hover:bg-blue-600"
          >
            {t("trends.week")}
          </button>
          <button
            onClick={() => applyRange(30)}
            className="px-3 py-1 rounded bg-blue-500 text-white hover:bg-blue-600"
          >
            {t("trends.month")}
          </button>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="history-year">
            {t("trends.year")}
          </label>
          <select
            id="history-year"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="border p-2 rounded"
            disabled={availableYears.length === 0}
          >
            {availableYears.length === 0 ? (
              <option value="">{t("common.unavailable")}</option>
            ) : (
              availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))
            )}
          </select>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium text-gray-700">{t("trends.quarter")}</span>
          {[1, 2, 3, 4].map((quarter) => {
            const isAvailable = isQuarterAvailable(quarter - 1);
            return (
              <button
                key={quarter}
                onClick={() => applyQuarter(quarter - 1)}
                disabled={!isAvailable}
                className="px-3 py-1 rounded bg-blue-500 text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                Q{quarter}
              </button>
            );
          })}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">{t("trends.start")}</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setIsQuarterRange(false);
            }}
            className="border p-2 rounded"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">{t("trends.end")}</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setIsQuarterRange(false);
            }}
            className="border p-2 rounded"
          />
        </div>
      </div>

      {/* Filter controls */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
        <div>
          <label className="block mb-1 font-medium">{t("trends.location")}</label>
          {isQuarterRange && (
            <p className="text-xs text-gray-600 mb-1">
              {t("trends.quarterLocationLimit")}
            </p>
          )}
          <div className="flex gap-2 mb-1">
            <button
              type="button"
              onClick={() => showLocationList("scheduled")}
              disabled={isLoadingLocations}
              className="bg-blue-500 text-white px-2 py-1 rounded text-sm hover:bg-blue-600 disabled:opacity-60"
            >
              {t("trends.locations.scheduled")}
            </button>
            <button
              type="button"
              onClick={() => showLocationList("history")}
              disabled={isLoadingLocations}
              className="bg-blue-500 text-white px-2 py-1 rounded text-sm hover:bg-blue-600 disabled:opacity-60"
            >
              {t("trends.locations.all")}
            </button>
            <button
              onClick={() => handleClearAll(setSelectedLocations)}
              className="bg-gray-300 text-gray-800 px-2 py-1 rounded text-sm hover:bg-gray-400"
            >
              {t("common.clear")}
            </button>
          </div>
          <input value={locationSearchInput} onChange={(event) => setLocationSearchInput(event.target.value)} placeholder={t("trends.locations.searchPlaceholder")} className="border p-2 rounded w-full text-sm" />
          <div className="mt-2 max-h-48 overflow-y-auto rounded border bg-white">
            {isLoadingLocations && <p className="p-2 text-sm text-gray-500">{t("common.loading")}</p>}
            {!isLoadingLocations && locations.map((loc) => (
              <label key={loc.id} className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100">
                <input type="checkbox" checked={selectedLocations.includes(loc.id)} onChange={() => toggleLocation(loc.id)} />
                <span>{loc.label}</span>
              </label>
            ))}
          </div>
          <p className="mt-2 text-xs font-medium text-gray-600">{t("trends.locations.selected")}</p>
          <div className="mt-1 min-h-8 rounded border bg-gray-50 p-1">
            {selectedLocations.length > 0 ? selectedLocations.map((id) => (
              <button key={id} type="button" onClick={() => toggleLocation(id)} className="m-0.5 rounded bg-blue-100 px-2 py-1 text-left text-xs text-blue-900 hover:bg-blue-200">{locations.find((location) => location.id === id)?.label || id} ×</button>
            )) : <p className="px-1 py-1 text-xs text-gray-500">{t("trends.locations.noneSelected")}</p>}
          </div>
        </div>
        <MultiSelection title={t("trends.provider")} options={providers.map((id) => ({ id, label: PROVIDER_LABELS[id] || id }))} selected={selectedProviders} onToggle={(id) => toggleSelection(id, setSelectedProviders)} onSelectAll={() => handleSelectAll(providers, setSelectedProviders)} onClear={() => handleClearAll(setSelectedProviders)} allLabel={t("common.all")} clearLabel={t("common.clear")} selectedLabel={t("trends.selected")} noneSelectedLabel={t("trends.noneSelected")} />
        <MultiSelection title={t("trends.product")} options={availableProducts.map((id) => ({ id, label: id }))} selected={selectedProducts} onToggle={(id) => toggleSelection(id, setSelectedProducts)} onSelectAll={() => handleSelectAll(availableProducts, setSelectedProducts)} onClear={() => handleClearAll(setSelectedProducts)} allLabel={t("common.all")} clearLabel={t("common.clear")} selectedLabel={t("trends.selected")} noneSelectedLabel={t("trends.noneSelected")} />
        <MultiSelection title={t("trends.fuelCategory")} options={fuelCategories} selected={selectedCategories} onToggle={(id) => toggleSelection(id, setSelectedCategories)} onSelectAll={() => handleSelectAll(fuelCategories.map((item) => item.id), setSelectedCategories)} onClear={() => handleClearAll(setSelectedCategories)} allLabel={t("common.all")} clearLabel={t("common.clear")} selectedLabel={t("trends.selected")} noneSelectedLabel={t("trends.noneSelected")} />
        <MultiSelection title={t("trends.fuelType")} options={availableFuelTypes} selected={selectedFuelTypes} onToggle={(id) => toggleSelection(id, setSelectedFuelTypes)} onSelectAll={() => handleSelectAll(availableFuelTypes.map((item) => item.id), setSelectedFuelTypes)} onClear={() => handleClearAll(setSelectedFuelTypes)} allLabel={t("common.all")} clearLabel={t("common.clear")} selectedLabel={t("trends.selected")} noneSelectedLabel={t("trends.noneSelected")} />
      </div>

      {/* Line Chart */}
      <div className="flex justify-end">
        <button
          onClick={downloadChartSnapshot}
          disabled={chartData.length === 0}
          className="bg-blue-500 text-white px-3 py-2 rounded hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-gray-300"
        >
          {t("trends.saveChart")}
        </button>
      </div>
      <div ref={chartContainerRef} className="relative" style={{ width: "100%", height: 500 }}>
        <ResponsiveContainer>
          <LineChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            onMouseMove={(state) => {
              if (state?.activeLabel) setHoveredDate(state.activeLabel);
              if (Number.isFinite(state?.activeCoordinate?.x) && Number.isFinite(state?.activeCoordinate?.y)) {
                const width = chartContainerRef.current?.clientWidth || 0;
                setPointerPosition({
                  x: Math.max(8, Math.min(state.activeCoordinate.x + 12, Math.max(8, width - 280))),
                  y: Math.max(8, state.activeCoordinate.y - 28),
                });
              }
            }}
            onMouseLeave={() => { setHoveredSeriesKey(null); setPointerPosition(null); }}
          >
            <Brush
              dataKey="date"
              height={20}
              stroke="#3b82f6"
              travellerWidth={10}
              startIndex={Math.max(0, chartData.length - 30)}
              tickFormatter={() => ""}
            />
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={formatChartDate}
              minTickGap={36}
              height={28}
              tick={{ fontSize: 11 }}
            />
            <YAxis
              label={{ value: "€/L", angle: -90, position: "insideLeft" }}
              domain={["auto", "auto"]}
            />
            {chartSeries.map((series, index) => (
              <React.Fragment key={series.key}>
                <Line
                  type="monotone"
                  dataKey={series.key}
                  name={series.label}
                  stroke={seriesColors[index % seriesColors.length]}
                  dot={false}
                  strokeWidth={2}
                  isAnimationActive={false}
                  connectNulls
                />
                <Line
                  type="monotone"
                  dataKey={series.key}
                  stroke="transparent"
                  strokeWidth={14}
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                  connectNulls
                  legendType="none"
                  onMouseEnter={() => setHoveredSeriesKey(series.key)}
                  onMouseLeave={() => setHoveredSeriesKey(null)}
                />
              </React.Fragment>
            ))}
          </LineChart>
        </ResponsiveContainer>
        {hoveredLine && pointerPosition && (
          <div className="pointer-events-none absolute z-10 max-w-64 rounded border bg-white px-2 py-1 text-xs shadow" style={{ left: pointerPosition.x, top: pointerPosition.y }}>
            <span className="mr-1 inline-block h-2 w-2 rounded-full" style={{ backgroundColor: hoveredLine.color }} />
            {hoveredLine.label}: {hoveredLine.value.toFixed(3)} €/L
          </div>
        )}
      </div>
      <div className="overflow-x-auto rounded-xl border bg-white p-4">
        <h2 className="mb-3 text-lg font-semibold">{t("trends.dayValues")}{hoveredDate ? `: ${hoveredDate}` : ""}</h2>
        {hoveredValues.length > 0 ? (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr><th className="px-3 py-2 text-left">{t("trends.line")}</th><th className="px-3 py-2 text-right">€/L</th></tr>
            </thead>
            <tbody>
              {hoveredValues.map((item) => (
                <tr key={item.key} className="border-t">
                  <td className="px-3 py-2"><span className="mr-2 inline-block h-3 w-3 rounded-full" style={{ backgroundColor: item.color }} />{item.label}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{item.value.toFixed(3)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <p className="text-sm text-gray-500">{t("trends.noDayValues")}</p>}
      </div>
    </div>
  );
}
